import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "antd";
import Button from "../button";
import Icon from "../../assets/icons";
import { showToast } from "../toast";
import { RingLoader } from "react-spinners";
import useApi from "../hooks/request";
import FIlterSelect from "../filter-select";
import StepIndicator from "./indicator";
import RequisitionInformation from "./requisition-Information";
import { I_Requisition } from "../../pages/requisition/types";
import { I_Supplier } from "../../pages/suppliers/types";
import SupplierInformation from "./supplier-information";
import AddNewProduct from "./new-order";

interface AddOrderDialogProps {
  isVisible: boolean;
  onClose: () => void;
  requisition?: I_Requisition[];
  suppliers?: I_Supplier[];
  inventory?: any[];
}

// Define a consistent Product interface to be used throughout
interface Product {
  id: number;
  name: string;
  quantity: number;
  price: number;
  _id: string;
}

type FormStep = "RequisitionInformation" | "SupplierInformation" | "Products";

const AddPurchaseDialog: React.FC<AddOrderDialogProps> = ({
  isVisible,
  onClose,
  requisition = [],
  suppliers = [],
  inventory=[]
}) => {
  const [currentStep, setCurrentStep] = useState<FormStep>(
    "RequisitionInformation"
  );
  const [selectedReq, setSelectedReq] = useState<I_Requisition | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<I_Supplier | null>(null);
  const [orderProducts, setOrderProducts] = useState<Product[]>([
    { id: Date.now(), name: "", price: 0, _id: "", quantity: 0 },
  ]);

 
  const {
    handleSubmit,
    trigger,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const { loading, makeRequest } = useApi("/purchase-order", "POST");

  const handleRequisitionSelection = (requisitionId: string) => {
    const reqs = requisition.find((c) => c._id === requisitionId);

    if (reqs) {
      setSelectedReq(reqs);
    }
  };

  const handleSupplierSection = (supplierId: string) => {
    const supplier = suppliers.find((c) => c._id === supplierId);

    if (supplier) {
      setSelectedSupplier(supplier);
    }
  };

  // Create customer options for dropdown
  const requisitionOptions = requisition.map((requisitions) => ({
    value: requisitions?._id,
    label: requisitions?.createdBy?.username,
  }));

  const SupplierOptions = suppliers.map((supplier) => ({
    value: supplier?._id,
    label: supplier?.name,
  }));

  // Handle product changes from the AddNewProduct component
  const handleProductsChange = (products: Product[]) => {
    setOrderProducts(products);
  };

  const handleCreateRequisition = handleSubmit(async (formData) => {
    if (!selectedReq) {
      showToast("Please select a requisition", false, {
        position: "top-right",
      });
      return;
    }

    if (!selectedSupplier) {
      showToast("Please select a supplier", false, {
        position: "top-right",
      });
      return;
    }

    // Filter out any products that don't have an _id (weren't properly selected)
    const validProducts = orderProducts.filter((product) => product._id);

   

    const orderData = {
      purchaseRequisitionId: selectedReq._id,
      supplierId: selectedSupplier?._id,
      Items: validProducts.map((product) => ({
        _id: product._id,
        quantity: product.quantity,
        unitPrice: formData.price, 
      })),
      note: {
        transportationCost: formData.transportationCost || 0,
        packagingCost: formData.packagingCost || 0,
        loadingPoint: formData.loadingPoint || "",
        deliveryPoint: formData.deliveryPoint || ""
      }
    };

    const [response] = await makeRequest(orderData);

    if (response.status) {
      showToast(response.message || "Order created successfully", true, {
        position: "top-right",
      });
      onClose();
      reset();
      window.location.reload();
      setOrderProducts([
        { id: Date.now(), name: "", price: 0, _id: "", quantity: 0 },
      ]);
      setSelectedReq(null);
      setSelectedSupplier(null);
    } else {
      showToast(response?.message || "Failed to create order", false, {
        position: "top-right",
      });
    }
  });

  const validateAndProceed = async () => {
    let isValid = true;

    if (currentStep === "RequisitionInformation") {
      // Check if requisition is selected before proceeding
      if (!selectedReq) {
        showToast("Please select a requisition", false);
        return;
      }
      
      const fieldsToValidate = ["createdAt", "createdby", "urgencyLevel"];
      isValid = await trigger(fieldsToValidate);
    } else if (currentStep === "SupplierInformation") {
      // Check if supplier is selected before proceeding
      if (!selectedSupplier) {
        showToast("Please select a supplier", false);
        return;
      }
      
      const fieldsToValidate = ["deliveryMethod", "deliveryFee"];
      isValid = await trigger(fieldsToValidate);
    } else if (currentStep === "Products") {
      // For the Products step, we'd be submitting the form
      return handleCreateRequisition();
    }

    if (isValid) {
      if (currentStep === "RequisitionInformation") {
        setCurrentStep("SupplierInformation");
      } else if (currentStep === "SupplierInformation") {
        setCurrentStep("Products");
      }
    }
  };

  const goBackToPreviousStep = () => {
    if (currentStep === "SupplierInformation") {
      setCurrentStep("RequisitionInformation");
    } else if (currentStep === "Products") {
      setCurrentStep("SupplierInformation");
    }
  };

  const renderRequisitionSelection = () => {
    return (
      <div className="">
        <FIlterSelect
          name="purchaseRequisitionId"
          options={requisitionOptions}
          className="w-full"
          label="Purchase Requisition"
          onChange={(selectedValue) => {
            handleRequisitionSelection(selectedValue);
          }}
          value={selectedReq?._id || ""}
        />
      </div>
    );
  };

  const renderSupplierSelection = () => {
    return (
      <div className="">
        <FIlterSelect
          name="supplierId"
          options={SupplierOptions}
          className="w-full"
          label="Supplier"
          onChange={(selectedValue) => {
            handleSupplierSection(selectedValue);
          }}
          value={selectedSupplier?._id || ""}
        />
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "RequisitionInformation":
        return (
          <>
            {renderRequisitionSelection()}
            <RequisitionInformation
              control={control}
              errors={errors}
              reqs={selectedReq}
            />
          </>
        );
      case "SupplierInformation":
        return (
          <>
            {renderSupplierSelection()}
            <SupplierInformation
              control={control}
              errors={errors}
              supply={selectedSupplier}
            />
          </>
        );
      case "Products":
        return (
          <AddNewProduct
            control={control}
            errors={errors}
            reqs={selectedReq}
            initialProducts={orderProducts}
            onProductsChange={handleProductsChange}
          />
        );

      default:
        return null;
    }
  };

  const renderFooterButtons = () => {
    if (currentStep === "RequisitionInformation") {
      return (
        <div className="flex gap-3 py-2 mt-4">
          <Button
            type="button"
            className="hover:bg-green-700 flex items-center"
            onClick={validateAndProceed}
            variant="primary"
            size="lg"
          >
            <Icon name="noteIcon" />
            Proceed to Order Information
          </Button>
        </div>
      );
    } else if (currentStep === "SupplierInformation") {
      return (
        <div className="flex gap-2 items-center text-center py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 px-7 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            Back to Order Details
          </button>
          <Button
            type="button"
            size="lg"
            variant="primary"
            className="hover:bg-green-700 text-white flex"
            onClick={validateAndProceed}
          >
            <Icon name="noteIcon" />
            Proceed to Products
          </Button>
        </div>
      );
    } else if (currentStep === "Products") {
      return (
        <div className="flex gap-2 items-center text-center py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 px-7 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            Back to Supplier Information
          </button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="hover:bg-green-700 text-white flex items-center"
            onClick={handleCreateRequisition}
          >
            {loading ? (
              <RingLoader color="#ffffff" loading={loading} size={20} />
            ) : (
              <div className="flex gap-3">
               Save Purchase Order <Icon name="noteIcon" />
              </div>
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      visible={isVisible}
      onCancel={onClose}
       width="80%"
        centered={true}
        footer={null}
        style={{
          padding: 0,
          margin: 0,
          maxWidth: "1200px",
          height: "auto",
        }}
    >
      <h1 className="text-2xl font-bold -mt-2 mb-4">New Purchase Order</h1>
      <StepIndicator
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
      <form onSubmit={handleCreateRequisition}>
        <div className="grid gap-4">{renderStepContent()}</div>
        {renderFooterButtons()}
      </form>
    </Modal>
  );
};

export default AddPurchaseDialog;